import { v4 as uuid } from 'uuid';

import { CAROUSEL_BREAKPOINT, DRAG_MULTIPLIER, DRAG_THRESHOLD, KEY_REPEAT_THROTTLE_MS } from './carousel.constants';
import {
    clampIndex,
    focusFirstInteractiveInPanel,
    getDotsFromContainer,
    getPanelIndexFromTarget,
    getPanelsFromTrack,
    scrollTrackToPanel,
} from './carousel.helpers';

const generateCarouselUid = (): string => `sgw-mobile-carousel-${uuid()}`;

export interface CarouselCoreOptions {
    container: HTMLElement;
    track: HTMLDivElement;
    dots: HTMLDivElement | null;
    announcer: HTMLSpanElement | null;
    uid?: string;
    count: number;
    carouselRoleDescription?: string;
    containerRole?: string;
    containerLabel?: string;
    labelNavPanel?: string;
    labelStatusMsg?: string;
    breakpoint?: number;
    dragThreshold?: number;
    dragMultiplier?: number;
    /** @deprecated Kept for backwards compatibility. Active sync is now scroll-based. */
    intersectionThreshold?: number;
    keyRepeatThrottleMs?: number;
    /** Called whenever the active panel index changes, e.g. so React can re-render. */
    onActiveIndexChange?: (index: number) => void;
}

/**
 * Framework-agnostic carousel logic.
 *
 * Owns:
 * - ResizeObserver for carousel-mode detection
 * - Scroll-based active-panel tracking (rAF-throttled)
 * - All pointer-drag, keyboard, focus-in, and click event listeners
 * - ARIA semantics (applySemantics)
 *
 * Call destroy() to tear everything down (remove listeners, disconnect observers).
 */
export class CarouselCore {
    private readonly container: HTMLElement;

    private readonly track: HTMLDivElement;

    private readonly dots: HTMLDivElement | null;

    private readonly announcer: HTMLSpanElement | null;

    private readonly uid: string;

    private readonly count: number;

    private readonly carouselRoleDescription: string;

    private readonly containerRole?: string;

    private readonly containerLabel?: string;

    private readonly labelNavPanel: string;

    private readonly labelStatusMsg: string;

    private readonly breakpoint: number;

    private readonly dragThreshold: number;

    private readonly dragMultiplier: number;

    private readonly keyRepeatThrottleMs: number;

    private readonly onActiveIndexChange?: (index: number) => void;

    // Mutable state
    private activeIndex = 0;

    private isCarouselMode = true;

    private isDragging = false;

    private isDown = false;

    private hasCapturedPointer = false;

    private activePointerId: number | null = null;

    private startX = 0;

    private scrollLeft = 0;

    private lastTrackNavKeyTime = 0;

    private lastDotNavKeyTime = 0;

    private suppressNextClick = false;

    private resizeObserver: ResizeObserver | null = null;

    private scrollRafId: number | null = null;

    private readonly abortController = new AbortController();

    constructor(options: CarouselCoreOptions) {
        this.container = options.container;
        this.track = options.track;
        this.dots = options.dots;
        this.announcer = options.announcer;
        this.uid = options.uid ?? generateCarouselUid();
        this.count = options.count;
        this.carouselRoleDescription = options.carouselRoleDescription ?? 'Karusell';
        this.containerRole = options.containerRole;
        this.containerLabel = options.containerLabel;
        this.labelNavPanel = options.labelNavPanel ?? 'Naviger karusel';
        this.labelStatusMsg = options.labelStatusMsg ?? 'Viser kort';
        this.breakpoint = options.breakpoint ?? CAROUSEL_BREAKPOINT;
        this.dragThreshold = options.dragThreshold ?? DRAG_THRESHOLD;
        this.dragMultiplier = options.dragMultiplier ?? DRAG_MULTIPLIER;
        this.keyRepeatThrottleMs = options.keyRepeatThrottleMs ?? KEY_REPEAT_THROTTLE_MS;
        this.onActiveIndexChange = options.onActiveIndexChange;

        this.setElementIds();
        this.setupResizeObserver();
        this.setupEventListeners();
        this.applySemantics(0, this.isCarouselMode);
    }

    // Helpers

    private getPanels(): HTMLElement[] {
        return getPanelsFromTrack(this.track);
    }

    private getDots(): HTMLElement[] {
        return getDotsFromContainer(this.dots);
    }

    private isCarouselSemanticsMode(): boolean {
        return this.isCarouselMode;
    }

    /** Set the id attributes that ARIA cross-references rely on. */
    private setElementIds(): void {
        this.getPanels().forEach((panel, i) => {
            panel.id = `${this.uid}-panel-${i}`;
        });
        this.getDots().forEach((dot, i) => {
            dot.id = `${this.uid}-tab-${i}`;
        });
    }

    // ── Observers────

    private setupResizeObserver(): void {
        this.resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? this.container.offsetWidth;
            const next = width < this.breakpoint;
            this.isCarouselMode = next;
            this.applySemantics(this.activeIndex, next);
        });
        this.resizeObserver.observe(this.container);
    }

    // ── Event listeners──────

    private setupEventListeners(): void {
        const { signal } = this.abortController;

        this.track.addEventListener('pointerdown', this.handlePointerDown, { capture: true, signal });
        this.track.addEventListener('pointerup', this.handlePointerUpOrCancel, { capture: true, signal });
        this.track.addEventListener('pointercancel', this.handlePointerUpOrCancel, { capture: true, signal });
        // passive: false required so preventDefault() in pointermove is respected
        this.track.addEventListener('pointermove', this.handlePointerMove, {
            capture: true,
            passive: false,
            signal,
        } as AddEventListenerOptions);
        this.track.addEventListener('click', this.handleTrackClick, { signal });
        this.track.addEventListener('dragstart', this.handleTrackDragStart, { capture: true, signal });
        this.track.addEventListener('keydown', this.handleTrackKeyDown, { capture: true, signal });
        this.track.addEventListener('focusin', this.handleTrackFocusIn, { signal });
        this.track.addEventListener('scroll', this.handleTrackScroll, {
            passive: true,
            signal,
        } as AddEventListenerOptions);

        this.dots?.addEventListener('keydown', this.handleDotsKeyDown, { signal });

        // Per-dot click handlers are wired once; index is captured by closure.
        this.getDots().forEach((dot, i) => {
            dot.addEventListener('click', () => this.setActiveIndex(i, { scroll: true, focusFirstInteractive: true }), {
                signal,
            });
        });
    }

    // ── Semantics────

    applySemantics(nextIndex: number, carouselMode: boolean): void {
        const panels = this.getPanels();
        const dots = this.getDots();
        const semanticsEnabled = carouselMode;
        const hasDotNav = semanticsEnabled && dots.length > 1;
        const { activeElement } = document;

        panels.forEach((panel, i) => {
            if (hasDotNav) {
                panel.setAttribute('role', 'tabpanel');
                panel.setAttribute('aria-labelledby', `${this.uid}-tab-${i}`);
                const containsActiveFocus = activeElement instanceof HTMLElement && panel.contains(activeElement);
                if (i === nextIndex || containsActiveFocus) {
                    panel.removeAttribute('inert');
                } else {
                    panel.setAttribute('inert', '');
                }
            } else {
                panel.removeAttribute('role');
                panel.removeAttribute('aria-labelledby');
                panel.removeAttribute('inert');
            }
        });

        dots.forEach((dot, i) => {
            if (hasDotNav) {
                dot.setAttribute('role', 'tab');
                dot.setAttribute('aria-selected', i === nextIndex ? 'true' : 'false');
                dot.setAttribute('aria-controls', `${this.uid}-panel-${i}`);
                dot.setAttribute('tabindex', i === nextIndex ? '0' : '-1');
            } else {
                dot.removeAttribute('role');
                dot.removeAttribute('aria-selected');
                dot.removeAttribute('aria-controls');
                dot.removeAttribute('tabindex');
            }
        });

        if (this.dots) {
            if (hasDotNav) {
                this.dots.setAttribute('role', 'tablist');
                this.dots.setAttribute('aria-label', this.labelNavPanel);
            } else {
                this.dots.removeAttribute('role');
                this.dots.removeAttribute('aria-label');
            }
        }

        if (this.announcer) {
            if (semanticsEnabled && this.count > 1) {
                this.announcer.setAttribute('role', 'status');
                this.announcer.textContent = `${this.labelStatusMsg} ${nextIndex + 1}`;
            } else {
                this.announcer.removeAttribute('role');
                this.announcer.textContent = '';
            }
        }

        if (semanticsEnabled && this.count > 1) {
            this.container.setAttribute('aria-roledescription', this.carouselRoleDescription);
            if (this.containerRole) {
                this.container.setAttribute('role', this.containerRole);
            }
            if (this.containerLabel) {
                this.container.setAttribute('aria-label', this.containerLabel);
            }
        } else {
            this.container.removeAttribute('aria-roledescription');
            if (this.containerRole) {
                this.container.removeAttribute('role');
            }
            if (this.containerLabel) {
                this.container.removeAttribute('aria-label');
            }
        }
    }

    setActiveIndex(index: number, options: { scroll?: boolean; focusFirstInteractive?: boolean } = {}): void {
        const clamped = clampIndex(index, this.count);
        this.activeIndex = clamped;
        this.onActiveIndexChange?.(clamped);
        this.applySemantics(clamped, this.isCarouselMode);

        const panels = this.getPanels();
        const target = panels[clamped];
        if (!target) return;

        if (options.scroll) {
            scrollTrackToPanel(this.track, target);
        }
        if (options.focusFirstInteractive) {
            focusFirstInteractiveInPanel(target);
        }
    }

    // ── Pointer drag handlers

    private readonly handlePointerDown = (e: PointerEvent): void => {
        if (!this.isCarouselSemanticsMode()) return;
        if (e.pointerType !== 'mouse' || e.button !== 0) return;

        this.activePointerId = e.pointerId;
        this.suppressNextClick = false;
        this.hasCapturedPointer = false;
        this.isDown = true;
        this.isDragging = false;
        this.startX = e.clientX;
        this.scrollLeft = this.track.scrollLeft;
    };

    private readonly handlePointerUpOrCancel = (e: PointerEvent): void => {
        if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return;

        if (
            this.activePointerId !== null &&
            this.hasCapturedPointer &&
            this.track.hasPointerCapture(this.activePointerId)
        ) {
            this.track.releasePointerCapture(this.activePointerId);
        }

        this.activePointerId = null;
        this.hasCapturedPointer = false;
        this.isDown = false;
        this.suppressNextClick = this.isDragging;
        this.isDragging = false;
        this.track.classList.remove('sgw-mobile-carousel__track--dragging');
    };

    private readonly handlePointerMove = (e: PointerEvent): void => {
        if (!this.isDown) return;
        if (this.activePointerId !== null && e.pointerId !== this.activePointerId) return;
        if (e.buttons % 2 !== 1) return;

        const deltaX = e.clientX - this.startX;
        if (!this.isDragging && Math.abs(deltaX) <= this.dragThreshold) {
            return;
        }

        if (!this.isDragging) {
            this.isDragging = true;
            if (this.activePointerId !== null && !this.hasCapturedPointer) {
                this.track.setPointerCapture(this.activePointerId);
                this.hasCapturedPointer = true;
            }
            this.track.classList.add('sgw-mobile-carousel__track--dragging');
        }

        e.preventDefault();
        const walk = deltaX * this.dragMultiplier;
        this.track.scrollLeft = this.scrollLeft - walk;
    };

    private readonly handleTrackDragStart = (e: DragEvent): void => {
        if (this.isCarouselSemanticsMode()) {
            e.preventDefault();
        }
    };

    private readonly handleTrackScroll = (): void => {
        if (!this.isCarouselSemanticsMode()) return;
        if (this.scrollRafId !== null) return;

        this.scrollRafId = requestAnimationFrame(() => {
            this.scrollRafId = null;
            this.syncActiveIndexFromScroll();
        });
    };

    private syncActiveIndexFromScroll(): void {
        const panels = this.getPanels();
        if (!panels.length) return;

        const trackRect = this.track.getBoundingClientRect();
        const trackCenterX = trackRect.left + trackRect.width / 2;

        const nextIndex = panels.reduce(
            (closest, panel, index) => {
                const panelRect = panel.getBoundingClientRect();
                const panelCenterX = panelRect.left + panelRect.width / 2;
                const distance = Math.abs(panelCenterX - trackCenterX);
                return distance < closest.distance ? { index, distance } : closest;
            },
            { index: this.activeIndex, distance: Number.POSITIVE_INFINITY },
        ).index;

        if (nextIndex !== this.activeIndex) {
            this.setActiveIndex(nextIndex);
        }
    }

    private readonly handleTrackClick = (e: MouseEvent): void => {
        if (!this.suppressNextClick) return;

        e.preventDefault();
        e.stopPropagation();
        this.suppressNextClick = false;
    };

    // ── Keyboard handlers────

    private readonly handleTrackKeyDown = (e: KeyboardEvent): void => {
        if (!this.isCarouselSemanticsMode()) return;

        const isNavigationKey = e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'Home' || e.key === 'End';

        if (e.key === 'Tab' || !isNavigationKey) return;

        e.preventDefault();
        e.stopPropagation();

        if (e.repeat) {
            const now = Date.now();
            if (now - this.lastTrackNavKeyTime < this.keyRepeatThrottleMs) return;
            this.lastTrackNavKeyTime = now;
        }

        const panels = this.getPanels();
        const focusedPanelIndex = getPanelIndexFromTarget(e.target, panels);
        const currentIndex = focusedPanelIndex === -1 ? this.activeIndex : focusedPanelIndex;
        if (currentIndex < 0) return;

        if (e.key === 'ArrowRight') {
            if (currentIndex < panels.length - 1) {
                this.setActiveIndex(currentIndex + 1, { scroll: true, focusFirstInteractive: true });
            }
        } else if (e.key === 'ArrowLeft') {
            if (currentIndex > 0) {
                this.setActiveIndex(currentIndex - 1, { scroll: true, focusFirstInteractive: true });
            }
        } else if (e.key === 'Home') {
            this.setActiveIndex(0, { scroll: true, focusFirstInteractive: true });
        } else if (e.key === 'End') {
            this.setActiveIndex(panels.length - 1, { scroll: true, focusFirstInteractive: true });
        }
    };

    private static getNextDotIndexForKey(key: string, dotCount: number, currentIndex: number): number {
        switch (key) {
            case 'ArrowRight':
                return Math.min(dotCount - 1, currentIndex + 1);
            case 'ArrowLeft':
                return Math.max(0, currentIndex - 1);
            case 'Home':
                return 0;
            case 'End':
                return dotCount - 1;
            default:
                return currentIndex;
        }
    }

    private readonly handleDotsKeyDown = (e: KeyboardEvent): void => {
        if (!this.isCarouselSemanticsMode()) return;

        const isNavigationKey = e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'Home' || e.key === 'End';

        if (e.key === 'Tab' || !isNavigationKey) return;

        e.preventDefault();

        if (e.repeat) {
            const now = Date.now();
            if (now - this.lastDotNavKeyTime < this.keyRepeatThrottleMs) return;
            this.lastDotNavKeyTime = now;
        }

        const dots = this.getDots();
        if (!dots.length) return;

        const next = CarouselCore.getNextDotIndexForKey(e.key, dots.length, this.activeIndex);

        this.setActiveIndex(next, { scroll: true });
        dots[next].focus();
    };

    // ── Focus handler

    private readonly handleTrackFocusIn = (e: FocusEvent): void => {
        if (!this.isCarouselSemanticsMode()) return;
        if (this.isDown) return;
        const panel = (e.target as HTMLElement).closest<HTMLElement>('[data-carousel-panel]');
        if (!panel) return;
        const idx = this.getPanels().indexOf(panel);
        if (idx !== -1) this.setActiveIndex(idx, { scroll: true });
    };

    // Teardown

    destroy(): void {
        this.abortController.abort();
        if (this.scrollRafId !== null) {
            cancelAnimationFrame(this.scrollRafId);
            this.scrollRafId = null;
        }
        this.resizeObserver?.disconnect();
    }
}
